import * as i0 from '@angular/core';
import { EventEmitter, inject, isDevMode, SimpleChange, HostListener, Input, Output, HostBinding, ViewChild, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import * as i1 from '@angular/platform-browser';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/forms';
import { FormsModule } from '@angular/forms';

var MoveTypes;
(function (MoveTypes) {
    MoveTypes["Move"] = "move";
    MoveTypes["Resize"] = "resize";
    MoveTypes["Pinch"] = "pinch";
})(MoveTypes || (MoveTypes = {}));

function base64ToFile(base64Image) {
    const split = base64Image.split(',');
    const type = split[0].replace('data:', '').replace(';base64', '');
    const byteString = atob(split[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type });
}
// black 2x1 JPEG, with the following meta information set:
// - EXIF Orientation: 6 (Rotated 90° CCW)
const testAutoOrientationImageURL = 'data:image/jpeg;base64,/9j/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAA' +
    'AAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
    'QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE' +
    'BAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAEAAgMBEQACEQEDEQH/x' +
    'ABKAAEAAAAAAAAAAAAAAAAAAAALEAEAAAAAAAAAAAAAAAAAAAAAAQEAAAAAAAAAAAAAAAA' +
    'AAAAAEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwA/8H//2Q==';
function supportsAutomaticRotation() {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            // Check if browser supports automatic image orientation:
            const supported = img.width === 1 && img.height === 2;
            resolve(supported);
        };
        img.src = testAutoOrientationImageURL;
    });
}
function getTransformationsFromExifData(exifRotationOrBase64Image) {
    if (typeof exifRotationOrBase64Image === 'string') {
        exifRotationOrBase64Image = getExifRotation(exifRotationOrBase64Image);
    }
    switch (exifRotationOrBase64Image) {
        case 2:
            return { rotate: 0, flip: true };
        case 3:
            return { rotate: 2, flip: false };
        case 4:
            return { rotate: 2, flip: true };
        case 5:
            return { rotate: 1, flip: true };
        case 6:
            return { rotate: 1, flip: false };
        case 7:
            return { rotate: 3, flip: true };
        case 8:
            return { rotate: 3, flip: false };
        default:
            return { rotate: 0, flip: false };
    }
}
function getExifRotation(imageBase64) {
    const view = new DataView(base64ToArrayBuffer(imageBase64));
    if (view.getUint16(0, false) != 0xffd8) {
        return -2;
    }
    const length = view.byteLength;
    let offset = 2;
    while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8)
            return -1;
        const marker = view.getUint16(offset, false);
        offset += 2;
        if (marker == 0xffe1) {
            if (view.getUint32((offset += 2), false) != 0x45786966) {
                return -1;
            }
            const little = view.getUint16((offset += 6), false) == 0x4949;
            offset += view.getUint32(offset + 4, little);
            const tags = view.getUint16(offset, little);
            offset += 2;
            for (let i = 0; i < tags; i++) {
                if (view.getUint16(offset + i * 12, little) == 0x0112) {
                    return view.getUint16(offset + i * 12 + 8, little);
                }
            }
        }
        else if ((marker & 0xff00) != 0xff00) {
            break;
        }
        else {
            offset += view.getUint16(offset, false);
        }
    }
    return -1;
}
function base64ToArrayBuffer(imageBase64) {
    imageBase64 = imageBase64.replace(/^data\:([^\;]+)\;base64,/gim, '');
    const binaryString = atob(imageBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}
/*
 * Hermite resize - fast image resize/resample using Hermite filter.
 * https://github.com/viliusle/Hermite-resize
 */
function resizeCanvas(canvas, width, height) {
    const width_source = canvas.width;
    const height_source = canvas.height;
    width = Math.round(width);
    height = Math.round(height);
    const ratio_w = width_source / width;
    const ratio_h = height_source / height;
    const ratio_w_half = Math.ceil(ratio_w / 2);
    const ratio_h_half = Math.ceil(ratio_h / 2);
    const ctx = canvas.getContext('2d');
    if (ctx) {
        const img = ctx.getImageData(0, 0, width_source, height_source);
        const img2 = ctx.createImageData(width, height);
        const data = img.data;
        const data2 = img2.data;
        for (let j = 0; j < height; j++) {
            for (let i = 0; i < width; i++) {
                const x2 = (i + j * width) * 4;
                const center_y = j * ratio_h;
                let weight = 0;
                let weights = 0;
                let weights_alpha = 0;
                let gx_r = 0;
                let gx_g = 0;
                let gx_b = 0;
                let gx_a = 0;
                const xx_start = Math.floor(i * ratio_w);
                const yy_start = Math.floor(j * ratio_h);
                let xx_stop = Math.ceil((i + 1) * ratio_w);
                let yy_stop = Math.ceil((j + 1) * ratio_h);
                xx_stop = Math.min(xx_stop, width_source);
                yy_stop = Math.min(yy_stop, height_source);
                for (let yy = yy_start; yy < yy_stop; yy++) {
                    const dy = Math.abs(center_y - yy) / ratio_h_half;
                    const center_x = i * ratio_w;
                    const w0 = dy * dy; //pre-calc part of w
                    for (let xx = xx_start; xx < xx_stop; xx++) {
                        const dx = Math.abs(center_x - xx) / ratio_w_half;
                        const w = Math.sqrt(w0 + dx * dx);
                        if (w >= 1) {
                            //pixel too far
                            continue;
                        }
                        //hermite filter
                        weight = 2 * w * w * w - 3 * w * w + 1;
                        const pos_x = 4 * (xx + yy * width_source);
                        //alpha
                        gx_a += weight * data[pos_x + 3];
                        weights_alpha += weight;
                        //colors
                        if (data[pos_x + 3] < 255)
                            weight = (weight * data[pos_x + 3]) / 250;
                        gx_r += weight * data[pos_x];
                        gx_g += weight * data[pos_x + 1];
                        gx_b += weight * data[pos_x + 2];
                        weights += weight;
                    }
                }
                data2[x2] = gx_r / weights;
                data2[x2 + 1] = gx_g / weights;
                data2[x2 + 2] = gx_b / weights;
                data2[x2 + 3] = gx_a / weights_alpha;
            }
        }
        canvas.width = width;
        canvas.height = height;
        //draw
        ctx.putImageData(img2, 0, 0);
    }
}

class E11ImageCropperComponent {
    constructor(sanitizer, cd) {
        this.sanitizer = sanitizer;
        this.cd = cd;
        this.Hammer = typeof window !== 'undefined' ? window.Hammer : null;
        this.setImageMaxSizeRetries = 0;
        this.cropperScaledMinWidth = 20;
        this.cropperScaledMinHeight = 20;
        this.exifTransform = { rotate: 0, flip: false };
        this.autoRotateSupported = supportsAutomaticRotation();
        this.stepSize = 3;
        this.marginLeft = '0px';
        this.imageVisible = false;
        this.moveTypes = MoveTypes;
        this.format = 'png';
        this.maintainAspectRatio = true;
        this.transform = {};
        this.aspectRatio = 1;
        this.resizeToWidth = 0;
        this.resizeToHeight = 0;
        this.cropperMinWidth = 0;
        this.cropperMinHeight = 0;
        this.cropperStaticWidth = 0;
        this.cropperStaticHeight = 0;
        this.canvasRotation = 0;
        this.initialStepSize = 3;
        this.roundCropper = false;
        this.onlyScaleDown = false;
        this.imageQuality = 92;
        this.autoCrop = true;
        this.containWithinAspectRatio = false;
        this.hideResizeSquares = false;
        this.imageContainerClassOverrides = null;
        this.cropper = {
            x1: -100,
            y1: -100,
            x2: 10000,
            y2: 10000,
        };
        this.scale = 1;
        this.alignImage = 'center';
        this.disabled = false;
        this.imageCropped = new EventEmitter();
        this.startCropImage = new EventEmitter();
        this.imageLoaded = new EventEmitter();
        this.cropperReady = new EventEmitter();
        this.loadImageFailed = new EventEmitter();
        this.rotation = 0;
        this.isBrowser = inject(IS_BROWSER);
        this.initCropper();
    }
    ngOnChanges(changes) {
        if (this.cropperStaticHeight && this.cropperStaticWidth) {
            this.hideResizeSquares = true;
            this.cropperMinWidth = this.cropperStaticWidth;
            this.cropperMinHeight = this.cropperStaticHeight;
            this.maintainAspectRatio = false;
        }
        this.onChangesInputImage(changes);
        if (this.originalImage &&
            this.originalImage.complete &&
            this.exifTransform &&
            (changes['containWithinAspectRatio'] || changes['canvasRotation'])) {
            this.transformOriginalImage();
        }
        if (changes['cropper']) {
            this.setMaxSize();
            this.setCropperScaledMinSize();
            this.checkCropperPosition(false);
            this.doAutoCrop();
            this.cd.markForCheck();
        }
        if (changes['aspectRatio'] && this.imageVisible) {
            this.resetCropperPosition();
        }
        if (changes['transform']) {
            this.transform = this.transform || {};
            this.setCssTransform();
            this.doAutoCrop();
        }
    }
    onChangesInputImage(changes) {
        if (changes['imageChangedEvent'] || changes['imageURL'] || changes['imageBase64'] || changes['imageFile']) {
            this.initCropper();
        }
        if (changes['imageChangedEvent'] && this.isValidImageChangedEvent()) {
            this.loadImageFile(this.imageChangedEvent.target.files[0]);
        }
        if (changes['imageURL'] && this.imageURL) {
            this.loadImageFromURL(this.imageURL);
        }
        if (changes['imageBase64'] && this.imageBase64) {
            this.loadBase64Image(this.imageBase64);
        }
        if (changes['imageFile'] && this.imageFile) {
            this.loadImageFile(this.imageFile);
        }
    }
    isValidImageChangedEvent() {
        return (this.imageChangedEvent && this.imageChangedEvent.target && this.imageChangedEvent.target.files && this.imageChangedEvent.target.files.length > 0);
    }
    setCssTransform() {
        this.safeTransformStyle = this.sanitizer.bypassSecurityTrustStyle('scaleX(' +
            (this.transform.scale || 1) * (this.transform.flipH ? -1 : 1) +
            ')' +
            'scaleY(' +
            (this.transform.scale || 1) * (this.transform.flipV ? -1 : 1) +
            ')' +
            'rotate(' +
            (this.transform.rotate || 0) +
            'deg)');
    }
    ngOnInit() {
        this.stepSize = this.initialStepSize;
        this.activatePinchGesture();
    }
    initCropper() {
        this.imageVisible = false;
        this.transformedImage = null;
        this.safeImgDataUrl =
            'data:image/png;base64,iVBORw0KGg' + 'oAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQYV2NgAAIAAAU' + 'AAarVyFEAAAAASUVORK5CYII=';
        this.moveStart = {
            active: false,
            type: null,
            position: null,
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 0,
            clientX: 0,
            clientY: 0,
        };
        this.maxSize = {
            width: 0,
            height: 0,
        };
        this.originalSize = {
            width: 0,
            height: 0,
        };
        this.transformedSize = {
            width: 0,
            height: 0,
        };
        this.cropper.x1 = -100;
        this.cropper.y1 = -100;
        this.cropper.x2 = 10000;
        this.cropper.y2 = 10000;
    }
    loadImage(imageBase64, imageType) {
        if (this.isValidImageType(imageType)) {
            this.loadBase64Image(imageBase64);
        }
        else {
            this.loadImageFailed.emit();
        }
    }
    loadImageFile(file) {
        const fileReader = new FileReader();
        fileReader.onload = (event) => this.loadImage(event.target.result, file.type);
        fileReader.readAsDataURL(file);
    }
    isValidImageType(type) {
        return /image\/(png|jpg|jpeg|bmp|gif|tiff|webp)/.test(type);
    }
    loadBase64Image(imageBase64) {
        this.autoRotateSupported
            .then((supported) => this.checkExifAndLoadBase64Image(imageBase64, supported))
            .then(() => this.transformOriginalImage())
            .catch((error) => {
            this.loadImageFailed.emit();
            this.originalImage = null;
            this.originalBase64 = null;
            console.error(error);
        });
    }
    checkExifAndLoadBase64Image(imageBase64, autoRotateSupported) {
        return new Promise((resolve, reject) => {
            this.originalImage = new Image();
            this.originalImage.onload = () => {
                this.originalBase64 = imageBase64;
                this.exifTransform = getTransformationsFromExifData(autoRotateSupported ? -1 : imageBase64);
                if (this.originalImage) {
                    this.originalSize.width = this.originalImage?.naturalWidth;
                    this.originalSize.height = this.originalImage?.naturalHeight;
                }
                resolve();
            };
            this.originalImage.onerror = reject;
            this.originalImage.src = imageBase64;
        });
    }
    loadImageFromURL(url) {
        if (!this.isBrowser)
            return null;
        const img = new Image();
        img.onerror = () => this.loadImageFailed.emit();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            context.drawImage(img, 0, 0);
            this.loadBase64Image(canvas.toDataURL());
        };
        img.crossOrigin = 'anonymous';
        img.src = url;
    }
    transformOriginalImage() {
        if (!this.originalImage || !this.originalImage.complete || !this.exifTransform) {
            return Promise.reject(new Error('No image loaded'));
        }
        const transformedBase64 = this.transformImageBase64();
        return this.setTransformedImage(transformedBase64);
    }
    transformImageBase64() {
        if (!this.isBrowser)
            return null;
        const canvasRotation = this.canvasRotation + this.exifTransform.rotate;
        if (canvasRotation === 0 && !this.exifTransform.flip && !this.containWithinAspectRatio && this.originalBase64) {
            return this.originalBase64;
        }
        const transformedSize = this.getTransformedSize();
        const canvas = document.createElement('canvas');
        canvas.width = transformedSize.width;
        canvas.height = transformedSize.height;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(this.exifTransform.flip ? -1 : 1, 0, 0, 1, canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI * (canvasRotation / 2));
        if (this.originalImage) {
            ctx.drawImage(this.originalImage, -this.originalSize.width / 2, -this.originalSize.height / 2);
        }
        return canvas.toDataURL();
    }
    getTransformedSize() {
        const canvasRotation = this.canvasRotation + this.exifTransform.rotate;
        if (this.containWithinAspectRatio) {
            if (canvasRotation % 2) {
                const minWidthToContain = this.originalSize.width * this.aspectRatio;
                const minHeightToContain = this.originalSize.height / this.aspectRatio;
                return {
                    width: Math.max(this.originalSize.height, minWidthToContain),
                    height: Math.max(this.originalSize.width, minHeightToContain),
                };
            }
            else {
                const minWidthToContain = this.originalSize.height * this.aspectRatio;
                const minHeightToContain = this.originalSize.width / this.aspectRatio;
                return {
                    width: Math.max(this.originalSize.width, minWidthToContain),
                    height: Math.max(this.originalSize.height, minHeightToContain),
                };
            }
        }
        if (canvasRotation % 2) {
            return {
                height: this.originalSize.width,
                width: this.originalSize.height,
            };
        }
        return {
            width: this.originalSize.width,
            height: this.originalSize.height,
        };
    }
    setTransformedImage(transformedBase64) {
        return new Promise((resolve) => {
            this.transformedBase64 = transformedBase64;
            this.safeImgDataUrl = this.sanitizer.bypassSecurityTrustResourceUrl(transformedBase64);
            this.transformedImage = new Image();
            this.transformedImage.onload = () => {
                if (this.transformedImage) {
                    this.transformedSize.width = this.transformedImage.naturalWidth;
                    this.transformedSize.height = this.transformedImage.naturalHeight;
                }
                this.cd.markForCheck();
                resolve();
            };
            this.transformedImage.src = this.transformedBase64;
        });
    }
    imageLoadedInView() {
        if (this.transformedImage != null) {
            this.imageLoaded.emit();
            this.setImageMaxSizeRetries = 0;
            setTimeout(() => this.checkImageMaxSizeRecursively());
        }
    }
    checkImageMaxSizeRecursively() {
        if (this.setImageMaxSizeRetries > 40) {
            this.loadImageFailed.emit();
        }
        else if (this.sourceImageLoaded()) {
            this.setMaxSize();
            this.setCropperScaledMinSize();
            this.resetCropperPosition();
            this.cropperReady.emit({ ...this.maxSize });
            this.cd.markForCheck();
        }
        else {
            this.setImageMaxSizeRetries++;
            setTimeout(() => this.checkImageMaxSizeRecursively(), 50);
        }
    }
    sourceImageLoaded() {
        return this.sourceImage && this.sourceImage.nativeElement && this.sourceImage.nativeElement.offsetWidth > 0;
    }
    onResize() {
        if (!this.isBrowser)
            return null;
        this.resizeCropperPosition();
        this.setMaxSize();
        this.setCropperScaledMinSize();
    }
    activatePinchGesture() {
        if (this.Hammer) {
            const hammer = new this.Hammer(this.wrapper.nativeElement);
            hammer.get('pinch').set({ enable: true });
            hammer.on('pinchmove', this.onPinch.bind(this));
            hammer.on('pinchend', this.pinchStop.bind(this));
            hammer.on('pinchstart', this.startPinch.bind(this));
        }
        else if (isDevMode()) {
            console.warn("[NgxImageCropper] Could not find HammerJS - Pinch Gesture won't work");
        }
    }
    resizeCropperPosition() {
        const sourceImageElement = this.sourceImage.nativeElement;
        if (this.maxSize.width !== sourceImageElement.offsetWidth || this.maxSize.height !== sourceImageElement.offsetHeight) {
            this.cropper.x1 = (this.cropper.x1 * sourceImageElement.offsetWidth) / this.maxSize.width;
            this.cropper.x2 = (this.cropper.x2 * sourceImageElement.offsetWidth) / this.maxSize.width;
            this.cropper.y1 = (this.cropper.y1 * sourceImageElement.offsetHeight) / this.maxSize.height;
            this.cropper.y2 = (this.cropper.y2 * sourceImageElement.offsetHeight) / this.maxSize.height;
        }
    }
    resetCropperPosition() {
        const sourceImageElement = this.sourceImage.nativeElement;
        if (this.cropperStaticHeight && this.cropperStaticWidth) {
            this.cropper.x1 = 0;
            this.cropper.x2 = sourceImageElement.offsetWidth > this.cropperStaticWidth ? this.cropperStaticWidth : sourceImageElement.offsetWidth;
            this.cropper.y1 = 0;
            this.cropper.y2 = sourceImageElement.offsetHeight > this.cropperStaticHeight ? this.cropperStaticHeight : sourceImageElement.offsetHeight;
        }
        else {
            if (!this.maintainAspectRatio) {
                this.cropper.x1 = 0;
                this.cropper.x2 = sourceImageElement.offsetWidth;
                this.cropper.y1 = 0;
                this.cropper.y2 = sourceImageElement.offsetHeight;
            }
            else if (sourceImageElement.offsetWidth / this.aspectRatio < sourceImageElement.offsetHeight) {
                this.cropper.x1 = 0;
                this.cropper.x2 = sourceImageElement.offsetWidth;
                const cropperHeight = sourceImageElement.offsetWidth / this.aspectRatio;
                this.cropper.y1 = (sourceImageElement.offsetHeight - cropperHeight) / 2;
                this.cropper.y2 = this.cropper.y1 + cropperHeight;
            }
            else {
                this.cropper.y1 = 0;
                this.cropper.y2 = sourceImageElement.offsetHeight;
                const cropperWidth = sourceImageElement.offsetHeight * this.aspectRatio;
                this.cropper.x1 = (sourceImageElement.offsetWidth - cropperWidth) / 2;
                this.cropper.x2 = this.cropper.x1 + cropperWidth;
            }
        }
        this.doAutoCrop();
        this.imageVisible = true;
    }
    keyboardAccess(event) {
        this.changeKeyboardStepSize(event);
        this.keyboardMoveCropper(event);
    }
    changeKeyboardStepSize(event) {
        if (event.key >= '1' && event.key <= '9') {
            this.stepSize = +event.key;
            return;
        }
    }
    keyboardMoveCropper(event) {
        const keyboardWhiteList = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];
        if (!keyboardWhiteList.includes(event.key)) {
            return;
        }
        const moveType = event.shiftKey ? MoveTypes.Resize : MoveTypes.Move;
        const position = event.altKey ? this.getInvertedPositionForKey(event.key) : this.getPositionForKey(event.key);
        const moveEvent = this.getEventForKey(event.key, this.stepSize);
        event.preventDefault();
        event.stopPropagation();
        this.startMove({ clientX: 0, clientY: 0 }, moveType, position);
        this.moveImg(moveEvent);
        this.moveStop();
    }
    getPositionForKey(key) {
        switch (key) {
            case 'ArrowUp':
                return 'top';
            case 'ArrowRight':
                return 'right';
            case 'ArrowDown':
                return 'bottom';
            case 'ArrowLeft':
            default:
                return 'left';
        }
    }
    getInvertedPositionForKey(key) {
        switch (key) {
            case 'ArrowUp':
                return 'bottom';
            case 'ArrowRight':
                return 'left';
            case 'ArrowDown':
                return 'top';
            case 'ArrowLeft':
            default:
                return 'right';
        }
    }
    getEventForKey(key, stepSize) {
        switch (key) {
            case 'ArrowUp':
                return { clientX: 0, clientY: stepSize * -1 };
            case 'ArrowRight':
                return { clientX: stepSize, clientY: 0 };
            case 'ArrowDown':
                return { clientX: 0, clientY: stepSize };
            case 'ArrowLeft':
            default:
                return { clientX: stepSize * -1, clientY: 0 };
        }
    }
    startMove(event, moveType, position = null) {
        if (this.moveStart && this.moveStart.active && this.moveStart.type === MoveTypes.Pinch) {
            return;
        }
        if (event.preventDefault) {
            event.preventDefault();
        }
        this.moveStart = {
            active: true,
            type: moveType,
            position,
            clientX: this.getClientX(event),
            clientY: this.getClientY(event),
            ...this.cropper,
        };
    }
    startPinch(event) {
        if (!this.safeImgDataUrl) {
            return;
        }
        if (event.preventDefault) {
            event.preventDefault();
        }
        this.moveStart = {
            active: true,
            type: MoveTypes.Pinch,
            position: 'center',
            clientX: this.cropper.x1 + (this.cropper.x2 - this.cropper.x1) / 2,
            clientY: this.cropper.y1 + (this.cropper.y2 - this.cropper.y1) / 2,
            ...this.cropper,
        };
    }
    moveImg(event) {
        if (this.moveStart.active) {
            if (event.stopPropagation) {
                event.stopPropagation();
            }
            if (event.preventDefault) {
                event.preventDefault();
            }
            if (this.moveStart.type === MoveTypes.Move) {
                this.move(event);
                this.checkCropperPosition(true);
            }
            else if (this.moveStart.type === MoveTypes.Resize) {
                if (!this.cropperStaticWidth && !this.cropperStaticHeight) {
                    this.resize(event);
                }
                this.checkCropperPosition(false);
            }
            this.cd.detectChanges();
        }
    }
    onPinch(event) {
        if (this.moveStart.active) {
            if (event.stopPropagation) {
                event.stopPropagation();
            }
            if (event.preventDefault) {
                event.preventDefault();
            }
            if (this.moveStart.type === MoveTypes.Pinch) {
                this.resize(event);
                this.checkCropperPosition(false);
            }
            this.cd.detectChanges();
        }
    }
    setMaxSize() {
        if (this.sourceImage) {
            const sourceImageElement = this.sourceImage.nativeElement;
            this.maxSize.width = sourceImageElement.offsetWidth;
            this.maxSize.height = sourceImageElement.offsetHeight;
            this.marginLeft = this.sanitizer.bypassSecurityTrustStyle('calc(50% - ' + this.maxSize.width / 2 + 'px)');
        }
    }
    setCropperScaledMinSize() {
        if (this.transformedImage) {
            this.setCropperScaledMinWidth();
            this.setCropperScaledMinHeight();
        }
        else {
            this.cropperScaledMinWidth = 20;
            this.cropperScaledMinHeight = 20;
        }
    }
    setCropperScaledMinWidth() {
        this.cropperScaledMinWidth =
            this.cropperMinWidth > 0 ? Math.max(20, this.cropperMinWidth / ((this.transformedImage?.width || 1) * this.maxSize.width)) : 20;
    }
    setCropperScaledMinHeight() {
        if (this.maintainAspectRatio) {
            this.cropperScaledMinHeight = Math.max(20, this.cropperScaledMinWidth / this.aspectRatio);
        }
        else if (this.cropperMinHeight > 0) {
            this.cropperScaledMinHeight = Math.max(20, this.cropperMinHeight / ((this.transformedImage?.height || 1) * this.maxSize.height));
        }
        else {
            this.cropperScaledMinHeight = 20;
        }
    }
    checkCropperPosition(maintainSize = false) {
        if (this.cropper.x1 < 0) {
            this.cropper.x2 -= maintainSize ? this.cropper.x1 : 0;
            this.cropper.x1 = 0;
        }
        if (this.cropper.y1 < 0) {
            this.cropper.y2 -= maintainSize ? this.cropper.y1 : 0;
            this.cropper.y1 = 0;
        }
        if (this.cropper.x2 > this.maxSize.width) {
            this.cropper.x1 -= maintainSize ? this.cropper.x2 - this.maxSize.width : 0;
            this.cropper.x2 = this.maxSize.width;
        }
        if (this.cropper.y2 > this.maxSize.height) {
            this.cropper.y1 -= maintainSize ? this.cropper.y2 - this.maxSize.height : 0;
            this.cropper.y2 = this.maxSize.height;
        }
    }
    moveStop() {
        if (this.moveStart.active) {
            this.moveStart.active = false;
            this.doAutoCrop();
        }
    }
    pinchStop() {
        if (this.moveStart.active) {
            this.moveStart.active = false;
            this.doAutoCrop();
        }
    }
    move(event) {
        const diffX = this.getClientX(event) - this.moveStart.clientX;
        const diffY = this.getClientY(event) - this.moveStart.clientY;
        this.cropper.x1 = this.moveStart.x1 + diffX;
        this.cropper.y1 = this.moveStart.y1 + diffY;
        this.cropper.x2 = this.moveStart.x2 + diffX;
        this.cropper.y2 = this.moveStart.y2 + diffY;
    }
    resize(event) {
        const diffX = this.getClientX(event) - this.moveStart.clientX;
        const diffY = this.getClientY(event) - this.moveStart.clientY;
        switch (this.moveStart.position) {
            case 'left':
                this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - this.cropperScaledMinWidth);
                break;
            case 'topleft':
                this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - this.cropperScaledMinWidth);
                this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - this.cropperScaledMinHeight);
                break;
            case 'top':
                this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - this.cropperScaledMinHeight);
                break;
            case 'topright':
                this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + this.cropperScaledMinWidth);
                this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - this.cropperScaledMinHeight);
                break;
            case 'right':
                this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + this.cropperScaledMinWidth);
                break;
            case 'bottomright':
                this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + this.cropperScaledMinWidth);
                this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + this.cropperScaledMinHeight);
                break;
            case 'bottom':
                this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + this.cropperScaledMinHeight);
                break;
            case 'bottomleft':
                this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - this.cropperScaledMinWidth);
                this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + this.cropperScaledMinHeight);
                break;
            case 'center':
                const scale = event.scale;
                const newWidth = Math.abs(this.moveStart.x2 - this.moveStart.x1) * scale;
                const newHeight = Math.abs(this.moveStart.y2 - this.moveStart.y1) * scale;
                const x1 = this.cropper.x1;
                const y1 = this.cropper.y1;
                this.cropper.x1 = Math.min(this.moveStart.clientX - newWidth / 2, this.cropper.x2 - this.cropperScaledMinWidth);
                this.cropper.y1 = Math.min(this.moveStart.clientY - newHeight / 2, this.cropper.y2 - this.cropperScaledMinHeight);
                this.cropper.x2 = Math.max(this.moveStart.clientX + newWidth / 2, x1 + this.cropperScaledMinWidth);
                this.cropper.y2 = Math.max(this.moveStart.clientY + newHeight / 2, y1 + this.cropperScaledMinHeight);
                break;
        }
        if (this.maintainAspectRatio) {
            this.checkAspectRatio();
        }
    }
    checkAspectRatio() {
        let overflowX = 0;
        let overflowY = 0;
        switch (this.moveStart.position) {
            case 'top':
                this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(0 - this.cropper.y1, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= overflowY * this.aspectRatio > overflowX ? overflowY * this.aspectRatio : overflowX;
                    this.cropper.y1 += overflowY * this.aspectRatio > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'bottom':
                this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= overflowY * this.aspectRatio > overflowX ? overflowY * this.aspectRatio : overflowX;
                    this.cropper.y2 -= overflowY * this.aspectRatio > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'topleft':
                this.cropper.y1 = this.cropper.y2 - (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(0 - this.cropper.x1, 0);
                overflowY = Math.max(0 - this.cropper.y1, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x1 += overflowY * this.aspectRatio > overflowX ? overflowY * this.aspectRatio : overflowX;
                    this.cropper.y1 += overflowY * this.aspectRatio > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'topright':
                this.cropper.y1 = this.cropper.y2 - (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(0 - this.cropper.y1, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= overflowY * this.aspectRatio > overflowX ? overflowY * this.aspectRatio : overflowX;
                    this.cropper.y1 += overflowY * this.aspectRatio > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'right':
            case 'bottomright':
                this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= overflowY * this.aspectRatio > overflowX ? overflowY * this.aspectRatio : overflowX;
                    this.cropper.y2 -= overflowY * this.aspectRatio > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'left':
            case 'bottomleft':
                this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(0 - this.cropper.x1, 0);
                overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x1 += overflowY * this.aspectRatio > overflowX ? overflowY * this.aspectRatio : overflowX;
                    this.cropper.y2 -= overflowY * this.aspectRatio > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'center':
                this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
                this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                const overflowX1 = Math.max(0 - this.cropper.x1, 0);
                const overflowX2 = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                const overflowY1 = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                const overflowY2 = Math.max(0 - this.cropper.y1, 0);
                if (overflowX1 > 0 || overflowX2 > 0 || overflowY1 > 0 || overflowY2 > 0) {
                    this.cropper.x1 += overflowY1 * this.aspectRatio > overflowX1 ? overflowY1 * this.aspectRatio : overflowX1;
                    this.cropper.x2 -= overflowY2 * this.aspectRatio > overflowX2 ? overflowY2 * this.aspectRatio : overflowX2;
                    this.cropper.y1 += overflowY2 * this.aspectRatio > overflowX2 ? overflowY2 : overflowX2 / this.aspectRatio;
                    this.cropper.y2 -= overflowY1 * this.aspectRatio > overflowX1 ? overflowY1 : overflowX1 / this.aspectRatio;
                }
                break;
        }
    }
    doAutoCrop() {
        if (this.autoCrop) {
            this.crop();
        }
    }
    crop() {
        if (!this.isBrowser)
            return null;
        if (this.sourceImage && this.sourceImage.nativeElement && this.transformedImage != null) {
            this.startCropImage.emit();
            const imagePosition = this.getImagePosition();
            const width = imagePosition.x2 - imagePosition.x1;
            const height = imagePosition.y2 - imagePosition.y1;
            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = width;
            cropCanvas.height = height;
            const ctx = cropCanvas.getContext('2d');
            if (ctx) {
                if (this.backgroundColor != null) {
                    ctx.fillStyle = this.backgroundColor;
                    ctx.fillRect(0, 0, width, height);
                }
                const scaleX = (this.transform.scale || 1) * (this.transform.flipH ? -1 : 1);
                const scaleY = (this.transform.scale || 1) * (this.transform.flipV ? -1 : 1);
                ctx.setTransform(scaleX, 0, 0, scaleY, this.transformedSize.width / 2, this.transformedSize.height / 2);
                ctx.translate(-imagePosition.x1 / scaleX, -imagePosition.y1 / scaleY);
                ctx.rotate(((this.transform.rotate || 0) * Math.PI) / 180);
                ctx.drawImage(this.transformedImage, -this.transformedSize.width / 2, -this.transformedSize.height / 2);
                const output = {
                    width,
                    height,
                    imagePosition,
                    cropperPosition: { ...this.cropper },
                };
                if (this.containWithinAspectRatio) {
                    output.offsetImagePosition = this.getOffsetImagePosition();
                }
                const resizeRatio = this.getResizeRatio(width, height);
                if (resizeRatio !== 1) {
                    output.width = Math.round(width * resizeRatio);
                    output.height = this.maintainAspectRatio ? Math.round(output.width / this.aspectRatio) : Math.round(height * resizeRatio);
                    resizeCanvas(cropCanvas, output.width, output.height);
                }
                output.base64 = this.cropToBase64(cropCanvas);
                output.file = base64ToFile(output.base64);
                this.imageCropped.emit(output);
                return output;
            }
        }
        return null;
    }
    getImagePosition() {
        const sourceImageElement = this.sourceImage.nativeElement;
        const ratio = this.transformedSize.width / sourceImageElement.offsetWidth;
        const out = {
            x1: Math.round(this.cropper.x1 * ratio),
            y1: Math.round(this.cropper.y1 * ratio),
            x2: Math.round(this.cropper.x2 * ratio),
            y2: Math.round(this.cropper.y2 * ratio),
        };
        if (!this.containWithinAspectRatio) {
            out.x1 = Math.max(out.x1, 0);
            out.y1 = Math.max(out.y1, 0);
            out.x2 = Math.min(out.x2, this.transformedSize.width);
            out.y2 = Math.min(out.y2, this.transformedSize.height);
        }
        return out;
    }
    getOffsetImagePosition() {
        const canvasRotation = this.canvasRotation + this.exifTransform.rotate;
        const sourceImageElement = this.sourceImage.nativeElement;
        const ratio = this.transformedSize.width / sourceImageElement.offsetWidth;
        let offsetX;
        let offsetY;
        if (canvasRotation % 2) {
            offsetX = (this.transformedSize.width - this.originalSize.height) / 2;
            offsetY = (this.transformedSize.height - this.originalSize.width) / 2;
        }
        else {
            offsetX = (this.transformedSize.width - this.originalSize.width) / 2;
            offsetY = (this.transformedSize.height - this.originalSize.height) / 2;
        }
        const out = {
            x1: Math.round(this.cropper.x1 * ratio) - offsetX,
            y1: Math.round(this.cropper.y1 * ratio) - offsetY,
            x2: Math.round(this.cropper.x2 * ratio) - offsetX,
            y2: Math.round(this.cropper.y2 * ratio) - offsetY,
        };
        if (!this.containWithinAspectRatio) {
            out.x1 = Math.max(out.x1, 0);
            out.y1 = Math.max(out.y1, 0);
            out.x2 = Math.min(out.x2, this.transformedSize.width);
            out.y2 = Math.min(out.y2, this.transformedSize.height);
        }
        return out;
    }
    cropToBase64(cropCanvas) {
        return cropCanvas.toDataURL('image/' + this.format, this.getQuality());
    }
    getQuality() {
        return Math.min(1, Math.max(0, this.imageQuality / 100));
    }
    getResizeRatio(width, height) {
        const ratioWidth = this.resizeToWidth / width;
        const ratioHeight = this.resizeToHeight / height;
        const ratios = new Array();
        if (this.resizeToWidth > 0) {
            ratios.push(ratioWidth);
        }
        if (this.resizeToHeight > 0) {
            ratios.push(ratioHeight);
        }
        const result = ratios.length === 0 ? 1 : Math.min(...ratios);
        if (result > 1 && !this.onlyScaleDown) {
            return result;
        }
        return Math.min(result, 1);
    }
    getClientX(event) {
        return (event.touches && event.touches[0] ? event.touches[0].clientX : event.clientX) || 0;
    }
    getClientY(event) {
        return (event.touches && event.touches[0] ? event.touches[0].clientY : event.clientY) || 0;
    }
    rotateLeft() {
        this.canvasRotation--;
        this.flipAfterRotate();
    }
    rotateRight() {
        this.canvasRotation++;
        this.flipAfterRotate();
    }
    flipAfterRotate() {
        const flippedH = this.transform.flipH;
        const flippedV = this.transform.flipV;
        this.transform = {
            ...this.transform,
            flipH: flippedV,
            flipV: flippedH,
        };
        this.reflectAfterChange('canvasRotation', this.canvasRotation);
        this.reflectAfterChange('transform', this.transform);
    }
    flipHorizontal() {
        this.transform = {
            ...this.transform,
            flipH: !this.transform.flipH,
        };
        this.reflectAfterChange('transform', this.transform);
    }
    flipVertical() {
        this.transform = {
            ...this.transform,
            flipV: !this.transform.flipV,
        };
        this.reflectAfterChange('transform', this.transform);
    }
    resetImage() {
        this.scale = 1;
        this.rotation = 0;
        this.canvasRotation = 0;
        this.transform = {};
        this.reflectAfterChange('transform', this.transform);
        this.reflectAfterChange('canvasRotation', this.canvasRotation);
    }
    zoomOut() {
        this.scale -= 0.1;
        this.transform = {
            ...this.transform,
            scale: this.scale,
        };
        this.reflectAfterChange('transform', this.transform);
    }
    zoomIn() {
        this.scale += 0.1;
        this.transform = {
            ...this.transform,
            scale: this.scale,
        };
        this.reflectAfterChange('transform', this.transform);
    }
    toggleContainWithinAspectRatio() {
        this.containWithinAspectRatio = !this.containWithinAspectRatio;
        this.reflectAfterChange('containWithinAspectRatio', this.containWithinAspectRatio);
    }
    updateRotation(updateView = false) {
        this.transform = {
            ...this.transform,
            rotate: this.rotation,
        };
        if (updateView) {
            return this.reflectAfterChange('transform', this.transform);
        }
        this.setCssTransform();
    }
    reflectAfterChange(name, currentValue) {
        this.ngOnChanges({ [name]: new SimpleChange(null, currentValue, false) });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageCropperComponent, deps: [{ token: i1.DomSanitizer }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ImageCropperComponent, selector: "e11-image-cropper", inputs: { imageChangedEvent: "imageChangedEvent", imageURL: "imageURL", imageBase64: "imageBase64", imageFile: "imageFile", format: "format", maintainAspectRatio: "maintainAspectRatio", transform: "transform", aspectRatio: "aspectRatio", resizeToWidth: "resizeToWidth", resizeToHeight: "resizeToHeight", cropperMinWidth: "cropperMinWidth", cropperMinHeight: "cropperMinHeight", cropperStaticWidth: "cropperStaticWidth", cropperStaticHeight: "cropperStaticHeight", canvasRotation: "canvasRotation", initialStepSize: "initialStepSize", roundCropper: "roundCropper", onlyScaleDown: "onlyScaleDown", imageQuality: "imageQuality", autoCrop: "autoCrop", backgroundColor: "backgroundColor", containWithinAspectRatio: "containWithinAspectRatio", hideResizeSquares: "hideResizeSquares", imageContainerClassOverrides: "imageContainerClassOverrides", cropper: "cropper", scale: "scale", alignImage: "alignImage", disabled: "disabled", rotation: "rotation" }, outputs: { imageCropped: "imageCropped", startCropImage: "startCropImage", imageLoaded: "imageLoaded", cropperReady: "cropperReady", loadImageFailed: "loadImageFailed" }, host: { listeners: { "window:resize": "onResize()", "document:mousemove": "moveImg($event)", "document:touchmove": "moveImg($event)", "document:mouseup": "moveStop()", "document:touchend": "moveStop()" }, properties: { "style.text-align": "this.alignImage", "class.disabled": "this.disabled" } }, viewQueries: [{ propertyName: "wrapper", first: true, predicate: ["wrapper"], descendants: true, static: true }, { propertyName: "sourceImage", first: true, predicate: ["sourceImage"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"e11-image-cropper wrapper\" [style.background]=\"imageVisible && backgroundColor\" #wrapper>\n  <div class=\"editor\">\n    <div class=\"image e11-flex e11-justify-center e11-items-center e11-overflow-scroll {{ imageContainerClassOverrides }}\">\n      <img\n        #sourceImage\n        class=\"source-image\"\n        *ngIf=\"safeImgDataUrl\"\n        [src]=\"safeImgDataUrl\"\n        [style.visibility]=\"imageVisible ? 'visible' : 'hidden'\"\n        [style.transform]=\"safeTransformStyle\"\n        (load)=\"imageLoadedInView()\"\n      />\n    </div>\n\n    <div\n      class=\"overlay\"\n      [style.width.px]=\"maxSize.width\"\n      [style.height.px]=\"maxSize.height\"\n      [style.margin-left]=\"alignImage === 'center' ? marginLeft : null\"\n    ></div>\n    <div\n      class=\"cropper\"\n      *ngIf=\"imageVisible\"\n      [class.rounded]=\"roundCropper\"\n      [style.top.px]=\"cropper.y1\"\n      [style.left.px]=\"cropper.x1\"\n      [style.width.px]=\"cropper.x2 - cropper.x1\"\n      [style.height.px]=\"cropper.y2 - cropper.y1\"\n      [style.margin-left]=\"alignImage === 'center' ? marginLeft : null\"\n      [style.visibility]=\"imageVisible ? 'visible' : 'hidden'\"\n      (keydown)=\"keyboardAccess($event)\"\n      tabindex=\"0\"\n    >\n      <div (mousedown)=\"startMove($event, moveTypes.Move)\" (touchstart)=\"startMove($event, moveTypes.Move)\" class=\"move\"></div>\n      <ng-container *ngIf=\"!hideResizeSquares\">\n        <span\n          class=\"resize topleft\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'topleft')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'topleft')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize top\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize topright\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'topright')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'topright')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize right\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize bottomright\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'bottomright')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'bottomright')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize bottom\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize bottomleft\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'bottomleft')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'bottomleft')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize left\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize-bar top\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'top')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'top')\"\n        >\n        </span>\n        <span\n          class=\"resize-bar right\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'right')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'right')\"\n        >\n        </span>\n        <span\n          class=\"resize-bar bottom\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'bottom')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'bottom')\"\n        >\n        </span>\n        <span\n          class=\"resize-bar left\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'left')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'left')\"\n        >\n        </span>\n      </ng-container>\n    </div>\n  </div>\n\n  <div class=\"controls e11-gap-4 e11-w-full e11-flex e11-justify-center e11-items-center e11-bg-skin-primary e11-text-skin-white e11-py-2\">\n    <div class=\"e11-py-3 e11-hidden md:e11-flex e11-items-center\">\n      <label class=\"e11-font-medium e11-mr-3\"> Rotate </label>\n      <input\n        type=\"range\"\n        [min]=\"-90\"\n        step=\"1\"\n        [max]=\"90\"\n        [(ngModel)]=\"rotation\"\n        (input)=\"updateRotation()\"\n        (change)=\"updateRotation(true)\"\n        class=\"e11-w-full e11-h-1 e11-bg-blue-100 e11-appearance-non\"\n      />\n    </div>\n    <button (click)=\"rotateLeft()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> rotate_90_degrees_ccw </span>\n    </button>\n    <button (click)=\"rotateRight()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> rotate_90_degrees_cw </span>\n    </button>\n\n    <button (click)=\"resetImage()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> replay </span>\n    </button>\n    <button (click)=\"zoomOut()\" type=\"button\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"e11-h-6 e11-w-6\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"#fff\" stroke-width=\"2\">\n        <path stroke-linecap=\"round\" stroke-linejoin=\"round\" d=\"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7\" />\n      </svg>\n    </button>\n    <button (click)=\"zoomIn()\" type=\"button\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"e11-h-6 e11-w-6\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"#fff\" stroke-width=\"2\">\n        <path stroke-linecap=\"round\" stroke-linejoin=\"round\" d=\"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7\" />\n      </svg>\n    </button>\n    <button (click)=\"flipHorizontal()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> flip </span>\n    </button>\n  </div>\n</div>\n", styles: [".e11-image-cropper .editor{display:flex;position:relative;width:100%;max-width:100%;max-height:100%;overflow:hidden;text-align:center}.e11-image-cropper .editor>div{width:100%;position:relative}.e11-image-cropper .editor>div img.source-image{max-width:100%;max-height:100%;transform-origin:center}.e11-image-cropper .editor .overlay{position:absolute;pointer-events:none;touch-action:none;outline:white solid 100vw;top:0;left:0}.e11-image-cropper .editor .cropper{position:absolute;display:flex;color:#53535c;background:transparent;outline:rgba(255,255,255,.3) solid 100vw;outline:var(--cropper-outline-color, rgba(255, 255, 255, .3)) solid 100vw;touch-action:none}@media (orientation: portrait){.e11-image-cropper .editor .cropper{outline-width:100vh}}.e11-image-cropper .editor .cropper:after{position:absolute;content:\"\";inset:0;pointer-events:none;border:dashed 1px;opacity:.75;color:inherit;z-index:1}.e11-image-cropper .editor .cropper .move{width:100%;cursor:move;border:1px solid rgba(255,255,255,.5)}.e11-image-cropper .editor .cropper:focus .move{border-color:#1e90ff;border-width:2px}.e11-image-cropper .editor .cropper .resize{position:absolute;display:inline-block;line-height:6px;padding:8px;opacity:.85;z-index:1}.e11-image-cropper .editor .cropper .resize .square{display:inline-block;background:#53535c;width:6px;height:6px;border:1px solid rgba(255,255,255,.5);box-sizing:content-box}.e11-image-cropper .editor .cropper .resize.topleft{top:-12px;left:-12px;cursor:nwse-resize}.e11-image-cropper .editor .cropper .resize.top{top:-12px;left:calc(50% - 12px);cursor:ns-resize}.e11-image-cropper .editor .cropper .resize.topright{top:-12px;right:-12px;cursor:nesw-resize}.e11-image-cropper .editor .cropper .resize.right{top:calc(50% - 12px);right:-12px;cursor:ew-resize}.e11-image-cropper .editor .cropper .resize.bottomright{bottom:-12px;right:-12px;cursor:nwse-resize}.e11-image-cropper .editor .cropper .resize.bottom{bottom:-12px;left:calc(50% - 12px);cursor:ns-resize}.e11-image-cropper .editor .cropper .resize.bottomleft{bottom:-12px;left:-12px;cursor:nesw-resize}.e11-image-cropper .editor .cropper .resize.left{top:calc(50% - 12px);left:-12px;cursor:ew-resize}.e11-image-cropper .editor .cropper .resize-bar{position:absolute;z-index:1}.e11-image-cropper .editor .cropper .resize-bar.top{top:-11px;left:11px;width:calc(100% - 22px);height:22px;cursor:ns-resize}.e11-image-cropper .editor .cropper .resize-bar.right{top:11px;right:-11px;height:calc(100% - 22px);width:22px;cursor:ew-resize}.e11-image-cropper .editor .cropper .resize-bar.bottom{bottom:-11px;left:11px;width:calc(100% - 22px);height:22px;cursor:ns-resize}.e11-image-cropper .editor .cropper .resize-bar.left{top:11px;left:-11px;height:calc(100% - 22px);width:22px;cursor:ew-resize}.e11-image-cropper .editor .cropper.rounded{outline-color:transparent}.e11-image-cropper .editor .cropper.rounded:after{border-radius:100%;box-shadow:0 0 0 100vw #ffffff4d;box-shadow:0 0 0 100vw var(--cropper-outline-color, rgba(255, 255, 255, .3))}@media (orientation: portrait){.e11-image-cropper .editor .cropper.rounded:after{box-shadow:0 0 0 100vh #ffffff4d;box-shadow:0 0 0 100vh var(--cropper-outline-color, rgba(255, 255, 255, .3))}}.e11-image-cropper .editor .cropper.rounded .move{border-radius:100%}.e11-image-cropper .editor.disabled .cropper .resize,.e11-image-cropper .editor.disabled .cropper .resize-bar,.e11-image-cropper .editor.disabled .cropper .move{display:none}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.RangeValueAccessor, selector: "input[type=range][formControlName],input[type=range][formControl],input[type=range][ngModel]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageCropperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-image-cropper', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"e11-image-cropper wrapper\" [style.background]=\"imageVisible && backgroundColor\" #wrapper>\n  <div class=\"editor\">\n    <div class=\"image e11-flex e11-justify-center e11-items-center e11-overflow-scroll {{ imageContainerClassOverrides }}\">\n      <img\n        #sourceImage\n        class=\"source-image\"\n        *ngIf=\"safeImgDataUrl\"\n        [src]=\"safeImgDataUrl\"\n        [style.visibility]=\"imageVisible ? 'visible' : 'hidden'\"\n        [style.transform]=\"safeTransformStyle\"\n        (load)=\"imageLoadedInView()\"\n      />\n    </div>\n\n    <div\n      class=\"overlay\"\n      [style.width.px]=\"maxSize.width\"\n      [style.height.px]=\"maxSize.height\"\n      [style.margin-left]=\"alignImage === 'center' ? marginLeft : null\"\n    ></div>\n    <div\n      class=\"cropper\"\n      *ngIf=\"imageVisible\"\n      [class.rounded]=\"roundCropper\"\n      [style.top.px]=\"cropper.y1\"\n      [style.left.px]=\"cropper.x1\"\n      [style.width.px]=\"cropper.x2 - cropper.x1\"\n      [style.height.px]=\"cropper.y2 - cropper.y1\"\n      [style.margin-left]=\"alignImage === 'center' ? marginLeft : null\"\n      [style.visibility]=\"imageVisible ? 'visible' : 'hidden'\"\n      (keydown)=\"keyboardAccess($event)\"\n      tabindex=\"0\"\n    >\n      <div (mousedown)=\"startMove($event, moveTypes.Move)\" (touchstart)=\"startMove($event, moveTypes.Move)\" class=\"move\"></div>\n      <ng-container *ngIf=\"!hideResizeSquares\">\n        <span\n          class=\"resize topleft\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'topleft')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'topleft')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize top\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize topright\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'topright')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'topright')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize right\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize bottomright\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'bottomright')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'bottomright')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize bottom\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize bottomleft\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'bottomleft')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'bottomleft')\"\n        >\n          <span class=\"square\"></span>\n        </span>\n        <span class=\"resize left\">\n          <span class=\"square\"></span>\n        </span>\n        <span\n          class=\"resize-bar top\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'top')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'top')\"\n        >\n        </span>\n        <span\n          class=\"resize-bar right\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'right')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'right')\"\n        >\n        </span>\n        <span\n          class=\"resize-bar bottom\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'bottom')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'bottom')\"\n        >\n        </span>\n        <span\n          class=\"resize-bar left\"\n          (mousedown)=\"startMove($event, moveTypes.Resize, 'left')\"\n          (touchstart)=\"startMove($event, moveTypes.Resize, 'left')\"\n        >\n        </span>\n      </ng-container>\n    </div>\n  </div>\n\n  <div class=\"controls e11-gap-4 e11-w-full e11-flex e11-justify-center e11-items-center e11-bg-skin-primary e11-text-skin-white e11-py-2\">\n    <div class=\"e11-py-3 e11-hidden md:e11-flex e11-items-center\">\n      <label class=\"e11-font-medium e11-mr-3\"> Rotate </label>\n      <input\n        type=\"range\"\n        [min]=\"-90\"\n        step=\"1\"\n        [max]=\"90\"\n        [(ngModel)]=\"rotation\"\n        (input)=\"updateRotation()\"\n        (change)=\"updateRotation(true)\"\n        class=\"e11-w-full e11-h-1 e11-bg-blue-100 e11-appearance-non\"\n      />\n    </div>\n    <button (click)=\"rotateLeft()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> rotate_90_degrees_ccw </span>\n    </button>\n    <button (click)=\"rotateRight()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> rotate_90_degrees_cw </span>\n    </button>\n\n    <button (click)=\"resetImage()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> replay </span>\n    </button>\n    <button (click)=\"zoomOut()\" type=\"button\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"e11-h-6 e11-w-6\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"#fff\" stroke-width=\"2\">\n        <path stroke-linecap=\"round\" stroke-linejoin=\"round\" d=\"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7\" />\n      </svg>\n    </button>\n    <button (click)=\"zoomIn()\" type=\"button\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"e11-h-6 e11-w-6\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"#fff\" stroke-width=\"2\">\n        <path stroke-linecap=\"round\" stroke-linejoin=\"round\" d=\"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7\" />\n      </svg>\n    </button>\n    <button (click)=\"flipHorizontal()\" type=\"button\">\n      <span class=\"material-icons-outlined e11-float-right\"> flip </span>\n    </button>\n  </div>\n</div>\n", styles: [".e11-image-cropper .editor{display:flex;position:relative;width:100%;max-width:100%;max-height:100%;overflow:hidden;text-align:center}.e11-image-cropper .editor>div{width:100%;position:relative}.e11-image-cropper .editor>div img.source-image{max-width:100%;max-height:100%;transform-origin:center}.e11-image-cropper .editor .overlay{position:absolute;pointer-events:none;touch-action:none;outline:white solid 100vw;top:0;left:0}.e11-image-cropper .editor .cropper{position:absolute;display:flex;color:#53535c;background:transparent;outline:rgba(255,255,255,.3) solid 100vw;outline:var(--cropper-outline-color, rgba(255, 255, 255, .3)) solid 100vw;touch-action:none}@media (orientation: portrait){.e11-image-cropper .editor .cropper{outline-width:100vh}}.e11-image-cropper .editor .cropper:after{position:absolute;content:\"\";inset:0;pointer-events:none;border:dashed 1px;opacity:.75;color:inherit;z-index:1}.e11-image-cropper .editor .cropper .move{width:100%;cursor:move;border:1px solid rgba(255,255,255,.5)}.e11-image-cropper .editor .cropper:focus .move{border-color:#1e90ff;border-width:2px}.e11-image-cropper .editor .cropper .resize{position:absolute;display:inline-block;line-height:6px;padding:8px;opacity:.85;z-index:1}.e11-image-cropper .editor .cropper .resize .square{display:inline-block;background:#53535c;width:6px;height:6px;border:1px solid rgba(255,255,255,.5);box-sizing:content-box}.e11-image-cropper .editor .cropper .resize.topleft{top:-12px;left:-12px;cursor:nwse-resize}.e11-image-cropper .editor .cropper .resize.top{top:-12px;left:calc(50% - 12px);cursor:ns-resize}.e11-image-cropper .editor .cropper .resize.topright{top:-12px;right:-12px;cursor:nesw-resize}.e11-image-cropper .editor .cropper .resize.right{top:calc(50% - 12px);right:-12px;cursor:ew-resize}.e11-image-cropper .editor .cropper .resize.bottomright{bottom:-12px;right:-12px;cursor:nwse-resize}.e11-image-cropper .editor .cropper .resize.bottom{bottom:-12px;left:calc(50% - 12px);cursor:ns-resize}.e11-image-cropper .editor .cropper .resize.bottomleft{bottom:-12px;left:-12px;cursor:nesw-resize}.e11-image-cropper .editor .cropper .resize.left{top:calc(50% - 12px);left:-12px;cursor:ew-resize}.e11-image-cropper .editor .cropper .resize-bar{position:absolute;z-index:1}.e11-image-cropper .editor .cropper .resize-bar.top{top:-11px;left:11px;width:calc(100% - 22px);height:22px;cursor:ns-resize}.e11-image-cropper .editor .cropper .resize-bar.right{top:11px;right:-11px;height:calc(100% - 22px);width:22px;cursor:ew-resize}.e11-image-cropper .editor .cropper .resize-bar.bottom{bottom:-11px;left:11px;width:calc(100% - 22px);height:22px;cursor:ns-resize}.e11-image-cropper .editor .cropper .resize-bar.left{top:11px;left:-11px;height:calc(100% - 22px);width:22px;cursor:ew-resize}.e11-image-cropper .editor .cropper.rounded{outline-color:transparent}.e11-image-cropper .editor .cropper.rounded:after{border-radius:100%;box-shadow:0 0 0 100vw #ffffff4d;box-shadow:0 0 0 100vw var(--cropper-outline-color, rgba(255, 255, 255, .3))}@media (orientation: portrait){.e11-image-cropper .editor .cropper.rounded:after{box-shadow:0 0 0 100vh #ffffff4d;box-shadow:0 0 0 100vh var(--cropper-outline-color, rgba(255, 255, 255, .3))}}.e11-image-cropper .editor .cropper.rounded .move{border-radius:100%}.e11-image-cropper .editor.disabled .cropper .resize,.e11-image-cropper .editor.disabled .cropper .resize-bar,.e11-image-cropper .editor.disabled .cropper .move{display:none}\n"] }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }, { type: i0.ChangeDetectorRef }], propDecorators: { wrapper: [{
                type: ViewChild,
                args: ['wrapper', { static: true }]
            }], sourceImage: [{
                type: ViewChild,
                args: ['sourceImage', { static: false }]
            }], imageChangedEvent: [{
                type: Input
            }], imageURL: [{
                type: Input
            }], imageBase64: [{
                type: Input
            }], imageFile: [{
                type: Input
            }], format: [{
                type: Input
            }], maintainAspectRatio: [{
                type: Input
            }], transform: [{
                type: Input
            }], aspectRatio: [{
                type: Input
            }], resizeToWidth: [{
                type: Input
            }], resizeToHeight: [{
                type: Input
            }], cropperMinWidth: [{
                type: Input
            }], cropperMinHeight: [{
                type: Input
            }], cropperStaticWidth: [{
                type: Input
            }], cropperStaticHeight: [{
                type: Input
            }], canvasRotation: [{
                type: Input
            }], initialStepSize: [{
                type: Input
            }], roundCropper: [{
                type: Input
            }], onlyScaleDown: [{
                type: Input
            }], imageQuality: [{
                type: Input
            }], autoCrop: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], containWithinAspectRatio: [{
                type: Input
            }], hideResizeSquares: [{
                type: Input
            }], imageContainerClassOverrides: [{
                type: Input
            }], cropper: [{
                type: Input
            }], scale: [{
                type: Input
            }], alignImage: [{
                type: HostBinding,
                args: ['style.text-align']
            }, {
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }, {
                type: Input
            }], imageCropped: [{
                type: Output
            }], startCropImage: [{
                type: Output
            }], imageLoaded: [{
                type: Output
            }], cropperReady: [{
                type: Output
            }], loadImageFailed: [{
                type: Output
            }], rotation: [{
                type: Input
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }], moveImg: [{
                type: HostListener,
                args: ['document:mousemove', ['$event']]
            }, {
                type: HostListener,
                args: ['document:touchmove', ['$event']]
            }], moveStop: [{
                type: HostListener,
                args: ['document:mouseup']
            }, {
                type: HostListener,
                args: ['document:touchend']
            }] } });

class E11ImageCropperModuleModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageCropperModuleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ImageCropperModuleModule, declarations: [E11ImageCropperComponent], imports: [CommonModule, FormsModule, E11UtilModule], exports: [E11ImageCropperComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageCropperModuleModule, imports: [CommonModule, FormsModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageCropperModuleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11ImageCropperComponent],
                    imports: [CommonModule, FormsModule, E11UtilModule],
                    exports: [E11ImageCropperComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11ImageCropperComponent, E11ImageCropperModuleModule, MoveTypes, base64ToFile, getTransformationsFromExifData, resizeCanvas, supportsAutomaticRotation };
